import React, {
  memo,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useImperativeHandle,
} from "react";
import { useI18n } from "Hooks";
import { useAIChatContext } from "../../context/AIChatContext";

// components
import ToddleDoc from "AppComponents/ToddleDoc";
import AITutorVideoModality from "AppComponents/AIComponents/AITutorVideoModality";
import AITutorVideoModalityV2 from "AppComponents/AIComponents/AITutorVideoModalityV2";
import Workbook from "AppComponents/Workbook";
import CodeEditorWrapper from "./CodeEditorWrapper";
import Loading from "UIComponents/Loading";
import CircleLoading from "UIComponents/CircleLoading";

// third-party
import _ from "lodash";
import { connect } from "react-redux";
import { saveAs } from "file-saver";
import classNames from "classnames";
import moment from "moment";

// constants
import {
  MODALITY_DOC_EDITOR_CONFIG,
  MODALITY_TYPE_INFO_MAPPING,
} from "UIComponents/AIChat/modules/Constants";
import {
  getAttachmentId,
  isModalityAttachmentType,
} from "UIComponents/AIChat/modules/Utils";
import { colors } from "Constants";

// utils
import {
  CODE_EDITOR_PROGRAMMING_LANGUAGES,
  AI_TUTOR_LEARNING_TOOLS,
} from "@toddle-edu/common-utils";
import {
  copyToClipboard,
  generateRandomId,
  WORKBOOK_IFRAME_ID_SELECTORS,
  WORKBOOK_MODE_TYPES,
} from "Utils";
import { getTimeSince } from "../../modules/Utils";
import {
  getModalityNewVersion,
  groupAttachmentsByPresetChain,
  getHasBlobImage,
} from "./Utils";
import { getIsReactPlayerVideoModalityEnabled } from "Utils/ACLStoreUtils";
import { getJsonToMarkdownCode, getMarkdownToJsonCode } from "Utils/AIUtils";

// modules
import { getUserInfo, setToastMsg } from "Login/modules/LoginModule";
import { getToddleDocAttachment } from "AppComponents/ToddleDoc/Modules/ToddleDocModule";
import {
  createAIModalityAttachment,
  createAITutorModality,
  updateAITutorModality,
  updateAttachment,
} from "UIComponents/AIChat/modules/AIChatModules";
import { getAttachmentModality } from "UIComponents/AIChat/modules/AIChatGraphqlHelpers";
import { getAttachment, uploadFileV2 } from "modules/Services";

// design system
import {
  IconButton,
  Dropdown,
  DropdownMenu,
  DSProvider,
  ButtonDisclosure,
  Tooltip,
  TextInput,
  Avatar,
  Button,
  Alert,
  Dialog,
} from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  PencilOutlined,
  ShareCurveOutlined,
  TickLargeOutlined,
  CaretOpenedFilled,
  DotsVerticalOutlined,
  ClockRecentsOutlined,
  ZoomOutOutlined,
  ZoomInOutlined,
  CollapseArrowsTwoOutlined,
} from "@toddle-edu/ds-icons";

// scss
import classes from "./ModalitiesViewer.scss";

const styles = {
  overlayStyle: {
    zIndex: 2000,
    width: "306px",
  },
  renameInputStyle: {
    width: "356px",
    backgroundColor: colors.surfaceWhite,
  },
  overlayInnerStyle: {
    maxWidth: "400px",
  },
  rotateBy90: { transform: "rotate(90deg)" },
  toddleDocContainerStyle: {
    height: "calc(100vh - 136px)",
  },
  olderVersionAlertStyle: {
    borderRadius: "0px",
  },
};

const BREAKPOINT_SCREEN_WIDTH = 900;
const WORKBOOK_VIEW_MODE_DISABLE_OPTIONS = ["DRAWING_MODALITY_TOOLBAR"];

const NODES_DISABLED_FOR_TODDLE_DOC = {
  heading4: true,
  heading5: true,
  heading6: true,
};

const NODE_CONFIG = {
  image: {
    comments: false,
  },
};

const DEFAULT_TAG_SIZES_FOR_TODDLE_DOC = {
  h1: "20px",
  h2: "16px",
  h3: "14px",
};

const dropdownOptions = t => [
  {
    key: "download",
    label: t("common:download"),
    icon: <DownloadOutlined />,
  },
  {
    key: "copy",
    label: t("common:copy"),
    icon: <CopyOutlined />,
  },
];

const ModalitiesViewer = memo(props => {
  const {
    source,
    onClose = _.noop,
    attachments = [],
    testId = "appComponents",
    onToggleModalityContainer = _.noop,
    containerStyle = {},
    isResizing = false,
    assessmentId,
    courseId,
    gradeId,

    // action creators
    updateAttachment,
    setToastMsg,
    createAIModalityAttachment,
    createAITutorModality,
    getToddleDocAttachment,
    getAttachmentModality,

    updateAITutorModality,

    // state
    userId,
    showAIAnalysisLoader,
  } = props;

  // ============= states ===========

  const [editedName, setEditedName] = useState(null);
  const [isWorkbookLoading, setIsWorkbookLoading] = useState(false);
  const [renameTitleWarning, setRenameTitleWarning] = useState(null);
  const [isScreenSmall, setIsScreenSmall] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < BREAKPOINT_SCREEN_WIDTH
      : false
  );
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [showSavedLoader, setShowSavedLoader] = useState(false);

  // ============= custom hooks ===========
  const { t } = useI18n();
  const {
    mode: aiChatMode,
    entitySubType,
    localAttachments,
    setLocalAttachments: onLocalAttachmentsChange = _.noop,
    selectedModalityId,
    setSelectedModalityId: onModalityChange = _.noop,
    setIsPendingAttachmentUpdate,
    workbookCompRef,
    conversation,
    videoPlayerRef,
    isPendingAttachmentUpdate,
    onSendMessage = _.noop,
    updateCallsQueueRef,
    currentAIState,
    modalitiesViewerFunctionsRef,
    showOlderVersionAlert = true,
    openingMessageResourceId,
  } = useAIChatContext() || {};

  // ============= refs ===========

  const createdAttachmentsMapRef = useRef(new Map());
  const debouncedUpdateRef = useRef(null);
  const modalityValueRef = useRef(null);
  const initialWorkbookUrlRef = useRef({
    url: null,
    attachmentId: null,
    previousAttachmentId: null,
  });
  // Add new ref to store attachment ID mapping
  const attachmentIdMappingRef = useRef(new Map());

  const myIframeIdRef = useRef(
    source === "STUDENT_PREVIEW"
      ? WORKBOOK_IFRAME_ID_SELECTORS.AI_TUTOR_STUDENT_PREVIEW
      : WORKBOOK_IFRAME_ID_SELECTORS.AI_TUTOR
  );

  // ============= variables ===========
  const isTeacherChat = entitySubType === "ai_tutor";

  const messages = [..._.get(conversation, "messages.edges", [])].reverse();
  const attachmentOptions = _(attachments).compact().uniqBy("id").value();

  const openingMessage = _.find(
    messages,
    ({ node }) => node?.metadata?.isOpeningMessage
  );

  const hasOpeningMsgAttachment =
    _.size(_.get(openingMessage, "node.attachments")) > 0;

  const options = useMemo(
    () =>
      _.compact(
        _.map(attachmentOptions, (attachment, index) => {
          const { id: key, name, title, createdAt, type, metadata } =
            attachment || {};

          const timeSince = getTimeSince(t, moment.utc(createdAt).format());

          if (!isModalityAttachmentType(attachment, true)) return null;

          const { label, icon: avatarIcon, color } =
            MODALITY_TYPE_INFO_MAPPING[type] || {};

          const subText = type ? `${timeSince} • ${t(label)}` : timeSince;

          // for version dropdown subtext
          let version = null;
          if (isTeacherChat) {
            // Count how many attachments of the same type appear before this one
            const sameTypeAttachments = _.filter(
              attachmentOptions.slice(0, index),
              att => att?.type === type
            );
            version = _.size(sameTypeAttachments) + 1;
          } else {
            version = _.get(metadata, "version", null);
          }

          let versionSubText = timeSince;
          if (!_.isNil(version)) {
            versionSubText = `${timeSince} • ${t("common:version")} ${version}`;
          }

          const icon = (
            <Avatar
              size={"large"}
              shape={"square"}
              iconType="icon"
              icon={avatarIcon}
              color={color}
            />
          );

          return {
            key,
            label: name || title || t("common:untitled"),
            subText,
            icon,
            versionSubText,
            ...attachment,
            metadata: {
              ...(metadata || {}),
              version,
            },
          };
        })
      ),
    [t, isTeacherChat, attachmentOptions]
  );

  const selectedAttachmentData = useMemo(() => {
    const currentOption = _.find(options, { key: selectedModalityId });

    // after sending message when temp id gets replaced with actual id
    // then for a split second selectedModalityId might not be found in options
    if (selectedModalityId && !currentOption) {
      return _.last(options);
    }

    return currentOption;
  }, [options, selectedModalityId]);

  const shouldUpdateAttachmentWithoutCreatingDuplicate =
    isTeacherChat &&
    aiChatMode === "create" &&
    openingMessageResourceId === selectedAttachmentData?.presetAttachment?.id;

  const groupedOptions = useMemo(() => {
    return groupAttachmentsByPresetChain({
      t,
      attachments: options,
      selectedAttachmentData,
    });
  }, [t, options, selectedAttachmentData]);

  const versionHistoryOptions = useMemo(() => {
    if (_.isEmpty(options)) return [];

    // Find the group containing the selected attachment
    const selectedGroup = _.find(groupedOptions, group =>
      _.includes(group?.allIds, selectedModalityId)
    );

    if (!selectedGroup || _.isEmpty(selectedGroup?.allIds)) return [];

    const versionOptions = _.chain(options)
      .filter(attachment => _.includes(selectedGroup.allIds, attachment.id))
      .map(attachment => {
        const { id: key, name, title, createdAt, versionSubText, metadata } =
          attachment || {};

        return {
          key,
          label: name || title || t("common:untitled"),
          subText: versionSubText,
          createdAt,
          metadata,
        };
      })
      .orderBy(
        [
          item => _.get(item, "version", 0),
          item => moment(_.get(item, "createdAt")).valueOf(),
        ],
        ["desc", "desc"]
      )
      .value();

    return [
      {
        label: t("common:version_history"),
        key: "VERSION_HISTORY",
        isItemGroup: true,
        options: [...versionOptions],
      },
    ];
  }, [t, options, groupedOptions, selectedModalityId]);

  // Check if the selected modality is the latest version
  const isLatestVersion = useMemo(() => {
    const versionOptions = _.get(versionHistoryOptions, "0.options");

    if (_.isEmpty(versionOptions)) return true;

    const latestVersionId = _.get(_.first(versionOptions), "key");
    return selectedModalityId === latestVersionId;
  }, [versionHistoryOptions, selectedModalityId]);

  // Determine if we should show the attachment in view-only mode
  const isAttachmentInViewOnlyMode = useMemo(() => {
    const lastOptionId = _.last(options)?.id;

    //  if the selected modality is not the last modality
    const isNotLastModality = lastOptionId !== selectedModalityId;

    const isPartOfLocalAttachments = _.some(localAttachments, {
      id: selectedModalityId,
    });

    if (
      _.startsWith(selectedModalityId, "NEW_") &&
      lastOptionId &&
      !_.startsWith(lastOptionId, "NEW_")
    ) {
      return false;
    }

    return !isPartOfLocalAttachments && isNotLastModality;
  }, [options, localAttachments, selectedModalityId]);

  const shouldCreateNewAttachment = useMemo(() => {
    return (
      !_.isEmpty(selectedModalityId) &&
      _.isEmpty(_.find(localAttachments, { id: selectedModalityId }))
    );
  }, [selectedModalityId, localAttachments]);

  // Keep a ref in sync so callbacks that capture an older closure can still
  // read the latest value without re-rendering.
  const shouldCreateNewAttachmentRef = useRef(shouldCreateNewAttachment);

  useEffect(() => {
    shouldCreateNewAttachmentRef.current = shouldCreateNewAttachment;
  }, [shouldCreateNewAttachment]);

  const attachmentId = getAttachmentId(selectedAttachmentData);
  const attachmentType = _.get(selectedAttachmentData, "type");
  const { id: modalityAttachmentId, content: modalityValue } =
    _.get(selectedAttachmentData, "modalityAttachment") || {};

  const isEditDisabled =
    _.isEmpty(attachmentId) || _.startsWith(attachmentId, "NEW_");

  const isEditingName = editedName !== null;

  const analysisToolEvaluation = _.find(conversation?.analysisToolEvaluation, {
    toolType: "ANALYSIS",
  });

  const isViewMode =
    isAttachmentInViewOnlyMode ||
    showAIAnalysisLoader || // if analysis is loading
    !_.isEmpty(analysisToolEvaluation?.toolResponses); // if analysis generated

  const mode = isViewMode ? "view" : aiChatMode;
  const isEditMode = _.includes(["edit", "create"], mode);

  // ============= modality update handler ===========

  const clearTrackedAttachment = useCallback(presetId => {
    if (presetId) {
      createdAttachmentsMapRef.current.delete(presetId);
    }
  }, []);

  const clearAllTrackedAttachments = useCallback(() => {
    createdAttachmentsMapRef.current.clear();
  }, []);

  const handleModalityUpdate = useCallback(
    params => {
      const {
        id,
        value,
        newAttachmentId = generateRandomId(),
        modalityAttachmentId = generateRandomId(),
        ...rest
      } = params;

      onLocalAttachmentsChange(prevAttachments => {
        const existingAttachmentIndex = _.findIndex(prevAttachments, { id });

        const selectedModality = _.find(
          attachments,
          attachment =>
            attachment.id === selectedModalityId || attachment.id === id
        );

        // Update existing attachment
        if (existingAttachmentIndex !== -1) {
          const updatedAttachments = [...prevAttachments];
          const existingAttachment =
            updatedAttachments[existingAttachmentIndex];
          const attachmentType = _.get(existingAttachment, "type");
          const existingModalityAttachment = _.get(
            existingAttachment,
            "modalityAttachment",
            {}
          );
          const isToddleDoc = attachmentType === "TODDLE_DOC";
          const modalityAttachment = {
            ...existingModalityAttachment,
            content: value,
            __typename: isToddleDoc ? "ToddleDoc" : "ModalityFile",
          };

          if (modalityAttachmentId) {
            modalityAttachment.id = modalityAttachmentId;
          }

          updatedAttachments[existingAttachmentIndex] = {
            ...existingAttachment,
            modalityAttachment,
            ...(rest || {}),
            // Preserve originalAttachmentId if it already exists
            originalAttachmentId:
              rest.originalAttachmentId ||
              existingAttachment.originalAttachmentId,
          };
          return updatedAttachments;
        }

        // Create new attachment
        const modalityType = _.get(selectedModality, "type", "TODDLE_DOC");
        const isToddleDoc = modalityType === "TODDLE_DOC";
        const modalityAttachment = {
          id: generateRandomId(),
          content: value,
          __typename: isToddleDoc ? "ToddleDoc" : "ModalityFile",
          ...(!isToddleDoc && {
            metadata:
              rest.customMetadata ||
              _.get(selectedModality, "modalityAttachment.metadata", {}),
          }),
        };

        if (modalityAttachmentId) {
          modalityAttachment.id = modalityAttachmentId;
        }

        const newAttachment = {
          ...(selectedModality || {}),
          id: newAttachmentId,
          type: modalityType,
          metadata: {
            ...(selectedModality?.metadata || {}),
          },
          modalityAttachment,
          presetAttachment: {
            id: _.get(selectedModality, "id", null),
          },
          createdAt: moment(),
          ...(rest || {}),
          __typename: "Attachment",
        };

        return [...prevAttachments, newAttachment];
      });
    },
    [onLocalAttachmentsChange, selectedModalityId, attachments]
  );

  const updateAttachmentWrapper = useCallback(
    async (
      id,
      type,
      modalityValue,
      modalityAttachmentId,
      updatePresetAttachment = false,
      updatedMetadata
    ) => {
      if (!id || _.startsWith(id, "NEW_") || _.isNil(modalityValue)) return;

      setIsPendingAttachmentUpdate(true);
      const updateId = generateRandomId();
      try {
        switch (type) {
          case "TODDLE_DOC": {
            await updateAttachment({
              id,
              toddleDoc: { value: modalityValue },
              ...(updatePresetAttachment && {
                metadata: { updatePresetAttachment: true },
              }),
            });
            break;
          }

          case "CODE_EDITOR": {
            if (
              !modalityAttachmentId ||
              _.startsWith(modalityAttachmentId, "NEW_")
            ) {
              return;
            }

            await updateAttachment({
              id,
              modalityFile: {
                id: modalityAttachmentId,
                content: modalityValue,
                metadata: updatedMetadata,
              },
              ...(updatePresetAttachment && {
                metadata: { updatePresetAttachment: true },
              }),
            });
            break;
          }

          case "WORKBOOK": {
            if (!_.isEmpty(modalityValue?.workbookUrl)) {
              return;
            }
            if (!Array.isArray(updateCallsQueueRef.current)) {
              updateCallsQueueRef.current = [];
            }
            updateCallsQueueRef.current.push(updateId);
            const updatedAttachment = await updateAITutorModality({
              id,
              jsonData: modalityValue,
              ...(updatePresetAttachment && {
                metadata: { updatePresetAttachment: true },
              }),
            });

            onLocalAttachmentsChange(prev =>
              _.map(prev, attachment => {
                if (
                  attachment?.originalAttachmentId === updatedAttachment?.id ||
                  attachment?.id === updatedAttachment?.id
                ) {
                  return {
                    ...attachment,
                    ..._.omit(updatedAttachment, ["modalityAttachment"]),
                    id: attachment.id,
                    originalAttachmentId: updatedAttachment?.id,
                  };
                }
                return attachment;
              })
            );
            break;
          }
        }
      } finally {
        if (
          Array.isArray(updateCallsQueueRef.current) &&
          _.includes(updateCallsQueueRef.current, updateId)
        ) {
          _.pull(updateCallsQueueRef.current, updateId);
        }
        const hasBlobImage = getHasBlobImage(modalityValue);
        if (!hasBlobImage) setIsPendingAttachmentUpdate(false);

        setShowSavedLoader(true);
      }
    },
    [
      updateAttachment,
      onLocalAttachmentsChange,
      updateAITutorModality,
      setIsPendingAttachmentUpdate,
    ]
  );

  // ============= button variants ===========
  const showWhiteTheme = ["CODE_EDITOR", "LINK", "VIDEO"].includes(
    attachmentType
  );

  const buttonVariant = "neutral";
  const buttonType = "plain";
  const tooltipTheme = showWhiteTheme ? "light" : "dark";

  // ============= side effects ===========

  useEffect(() => {
    const isWorkbookSelected =
      selectedAttachmentData &&
      _.get(selectedAttachmentData, "type") === "WORKBOOK";
    if (!isWorkbookSelected) {
      return;
    }
    window.__ACTIVE_WORKBOOK_IFRAME_ID = myIframeIdRef.current;
    return () => {
      if (window?.__ACTIVE_WORKBOOK_IFRAME_ID === myIframeIdRef.current) {
        delete window.__ACTIVE_WORKBOOK_IFRAME_ID;
      }
    };
  }, [_.get(selectedAttachmentData, "id", ""), source]);

  // Create debounced update function once
  useEffect(() => {
    debouncedUpdateRef.current = _.debounce(updateAttachmentWrapper, 1000);

    return () => {
      if (debouncedUpdateRef.current) {
        debouncedUpdateRef.current.flush();
      }
    };
  }, [updateAttachmentWrapper]);

  // Update attachment when relevant data changes
  useEffect(() => {
    if (
      debouncedUpdateRef.current &&
      attachmentId &&
      !shouldUpdateAttachmentWithoutCreatingDuplicate
    ) {
      debouncedUpdateRef.current(
        attachmentId,
        attachmentType,
        modalityValue,
        modalityAttachmentId
      );
    }
  }, [
    attachmentId,
    attachmentType,
    modalityValue,
    modalityAttachmentId,
    shouldUpdateAttachmentWithoutCreatingDuplicate,
  ]);

  useEffect(() => {
    // 1. check if selected option is valid
    const isOptionValid = _.some(options, { key: selectedModalityId });

    if (!isOptionValid) {
      onModalityChange(_.last(options)?.key || null);
      clearTrackedAttachment(selectedModalityId);
      return;
    }

    // 2. check if the tracked entry for the currently selected preset is
    //    still valid – i.e. the local attachment it produced still exists.
    const tracked = createdAttachmentsMapRef.current.get(selectedModalityId);
    if (tracked?.tempId) {
      const stillExists = _.some(options, { key: tracked.tempId });
      if (!stillExists) {
        clearTrackedAttachment(selectedModalityId);
      }
    }
  }, [selectedModalityId, options, onModalityChange, clearTrackedAttachment]);

  useEffect(() => {
    modalityValueRef.current = modalityValue;
  }, [modalityValue]);

  // Reset refs and states when showAIAnalysisLoader becomes true
  useEffect(() => {
    if (showAIAnalysisLoader) {
      clearAllTrackedAttachments();

      // Cancel any pending debounced update
      if (debouncedUpdateRef.current) {
        debouncedUpdateRef.current.flush();
      }

      // Reset states
      setEditedName(null);
      setIsWorkbookLoading(false);
    }
  }, [showAIAnalysisLoader, clearAllTrackedAttachments]);

  // Handle window resize to track screen size
  useEffect(() => {
    const handleResize = _.debounce(() => {
      setIsScreenSmall(window.innerWidth < BREAKPOINT_SCREEN_WIDTH);
    }, 200); // Debounce with a delay of 200ms

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      setIsScreenSmall(window.innerWidth < BREAKPOINT_SCREEN_WIDTH);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  useEffect(() => {
    if (showSavedLoader) {
      const timer = setTimeout(() => {
        setShowSavedLoader(false);
      }, 1000); // Hide the loader after 1 seconds

      return () => clearTimeout(timer);
    }
  }, [showSavedLoader]);

  const checkAndWorkbookFocusIn = useCallback(() => {
    const active = _.get(window, "__ACTIVE_WORKBOOK_IFRAME_ID");
    if (active && active !== myIframeIdRef.current) {
      // currently working since added on both focus in & out
      return;
    }
    if (!isEditMode) return;
    const workbookDOM = document.getElementById(myIframeIdRef.current);
    const parentContainer = workbookDOM?.closest?.(
      "#modality-viewer-container"
    );

    if (parentContainer) {
      if (document.activeElement == workbookDOM) {
        parentContainer.style.border = "1px solid var(--border-primary)";
      } else {
        parentContainer.style.border = "1px solid var(--border-secondary)";
      }
    }
  }, [isEditMode]);

  const checkAndWorkbookFocusOut = useCallback(() => {
    const active = _.get(window, "__ACTIVE_WORKBOOK_IFRAME_ID");
    if (active && active !== myIframeIdRef.current) {
      // currently working since added on both focus in & out
      return;
    }
    if (!isEditMode) return;
    //  added timeout to allow activeElement to update or else activeElement is shown as body always
    setTimeout(() => {
      const workbookDOM = document.getElementById(myIframeIdRef.current);
      const parentContainer = workbookDOM?.closest?.(
        "#modality-viewer-container"
      );

      if (parentContainer) {
        if (document.activeElement == workbookDOM) {
          parentContainer.style.border = "1px solid var(--border-primary)";
        }
        //else part not needed since focusin will handle removing border
      }
    }, 10);
  }, [isEditMode]);

  useEffect(() => {
    //use focusin  / focusout to detect focus inside workbook iframe
    window.addEventListener("focusin", checkAndWorkbookFocusIn);
    window.addEventListener("focusout", checkAndWorkbookFocusOut);
    return () => {
      window.removeEventListener("focusin", checkAndWorkbookFocusIn);
      window.removeEventListener("focusout", checkAndWorkbookFocusOut);
    };
  }, [checkAndWorkbookFocusIn, checkAndWorkbookFocusOut]);

  // Pass checkAndWorkbookFocusIn to parent via callback
  const checkAndClearWorkbookSelection = useCallback(() => {
    //check if workbook type is selected
    const isWorkbookSelected =
      _.get(selectedAttachmentData, "type") === "WORKBOOK";
    if (!isWorkbookSelected) return;

    if (!isEditMode) return;

    const workbookDOM = document.getElementById(myIframeIdRef.current);
    const parentContainer = workbookDOM?.closest?.(
      "#modality-viewer-container"
    );

    if (parentContainer && document.activeElement != workbookDOM) {
      //send msg to workbook to remove selection
      try {
        workbookDOM.contentWindow?.postMessage?.(
          JSON.stringify({ MESSAGE_TOKEN: "CLEAR_SELECTION" }),
          "*"
        );
      } catch (e) {}
    }
  }, [selectedAttachmentData, isEditMode]);

  useImperativeHandle(
    modalitiesViewerFunctionsRef,
    () => ({ checkAndClearWorkbookSelection }),
    []
  );

  // functions
  const onClickAttachment = ({ key }) => {
    onModalityChange(key);
    setIsDropdownVisible(false);
  };

  // ============= rename functionality ===========
  const handleStartEdit = () => {
    setEditedName(selectedAttachmentData?.label || "");
  };

  const handleCancelEdit = () => {
    setEditedName(null);
  };

  const toggleRenameWarning = () => {
    setRenameTitleWarning(prev => !prev);
  };

  const handleSaveEdit = async () => {
    const previousName = selectedAttachmentData?.label;
    const newName = _.trim(editedName);

    if (_.isEmpty(newName) || isEditDisabled) {
      return;
    }

    if (previousName === editedName) {
      setEditedName(null);
      return;
    }

    const isDuplicateName = _.some(
      options,
      option => option?.label === newName
    );

    if (isDuplicateName) {
      setRenameTitleWarning(true);
      return;
    }

    try {
      // Update local attachment name optimistically
      onLocalAttachmentsChange(prevAttachments => {
        return _.map(prevAttachments, attachment => {
          if (getAttachmentId(attachment) === attachmentId) {
            return { ...attachment, name: newName };
          }
          return attachment;
        });
      });

      setIsPendingAttachmentUpdate(true);

      setToastMsg({
        msg: "toastMsgs:successfully_updated",
        type: "success",
        position: "toast-bottom-left",
      });

      setEditedName(null);

      await updateAttachment({ name: newName, id: attachmentId });
    } catch (error) {
      if (error) {
        onLocalAttachmentsChange(prevAttachments => {
          return _.map(prevAttachments, attachment => {
            if (getAttachmentId(attachment) === attachmentId) {
              return { ...attachment, name: previousName };
            }
            return attachment;
          });
        });
      }

      setToastMsg({
        msg: "toastMsgs:something_went_wrong",
        type: "error",
        position: "toast-bottom-left",
      });
    } finally {
      setIsPendingAttachmentUpdate(false);
      setEditedName(null);
    }
  };

  const handleNameChange = e => {
    setEditedName(e.target.value);
  };

  // ============= code modality ===========

  const handleCopyCode = () => {
    const code = _.get(
      selectedAttachmentData,
      "modalityAttachment.content",
      ""
    );

    if (code) {
      copyToClipboard(code, () => {
        setToastMsg({
          msg: "toastMsgs:copied_capital",
          type: "success",
        });
      });
    }
  };

  const handleDownloadCode = () => {
    let code = _.get(selectedAttachmentData, "modalityAttachment.content", "");

    // strip markdown code block syntax if present
    const lines = _.split(code, "\n");
    if (_.startsWith(_.first(lines), "```") && _.last(lines) === "```") {
      code = lines.slice(1, -1).join("\n");
    }

    const language = _.get(
      selectedAttachmentData,
      "modalityAttachment.metadata.language"
    );

    const fileExtension = _.get(
      _.find(CODE_EDITOR_PROGRAMMING_LANGUAGES, { key: language }),
      "fileExtension"
    );

    if (!fileExtension) {
      return;
    }

    const fileName = `${_.get(
      selectedAttachmentData,
      "name",
      t("common:untitled")
    )}.${fileExtension}`;

    const codeBlob = new Blob([code], { type: "text/plain;charset=utf-8" });
    saveAs(codeBlob, fileName);
  };

  const handleLanguageChange = async ({ key }) => {
    const metadata = _.get(
      selectedAttachmentData,
      "modalityAttachment.metadata",
      {}
    );
    const currentValue = _.get(
      selectedAttachmentData,
      "modalityAttachment.content",
      ""
    );
    const { code: plainCode } = getMarkdownToJsonCode(currentValue);

    const updatedContent = getJsonToMarkdownCode({
      code: plainCode,
      language: key,
    });

    const updatedMetadata = { ...metadata, language: key };

    // If duplicate attachment is not already created, create one
    if (
      !shouldUpdateAttachmentWithoutCreatingDuplicate &&
      shouldCreateNewAttachmentRef.current
    ) {
      await createDuplicateAttachment(
        selectedAttachmentData?.id,
        currentValue,
        updatedMetadata
      );
    } else {
      // Update local attachment optimistically
      onLocalAttachmentsChange(prevAttachments => {
        return _.map(prevAttachments, attachment => {
          if (getAttachmentId(attachment) === attachmentId) {
            return {
              ...attachment,
              modalityAttachment: {
                ...attachment.modalityAttachment,
                metadata: updatedMetadata,
              },
            };
          }
          return attachment;
        });
      });

      setIsPendingAttachmentUpdate(true);
      try {
        await updateAttachmentWrapper(
          attachmentId,
          attachmentType,
          updatedContent,
          modalityAttachmentId,
          isTeacherChat,
          updatedMetadata
        );
      } finally {
        setIsPendingAttachmentUpdate(false);
      }
    }
  };

  // ============= duplicate attachment creation ===========

  const createDuplicateAttachment = useCallback(
    async (originalId, value, customMetadata = null) => {
      const presetAttachment = selectedAttachmentData?.id;

      const alreadyTracked = createdAttachmentsMapRef.current.get(
        presetAttachment
      );
      if (alreadyTracked?.tempId || _.startsWith(presetAttachment, "NEW_")) {
        return;
      }

      const attachmentType = _.get(selectedAttachmentData, "type");
      const newAttachmentRandomId = generateRandomId();
      const modalityAttachmentRandomId = generateRandomId();

      createdAttachmentsMapRef.current.set(presetAttachment, {
        tempId: newAttachmentRandomId,
        attachmentId: null, // filled after backend responds
        modalityAttachmentId: null,
      });

      let nextVersion;

      if (!isTeacherChat) {
        nextVersion = getModalityNewVersion(
          options,
          selectedAttachmentData?.id
        );
      }

      // show attachment above input box immediately
      handleModalityUpdate({
        id: originalId,
        value,
        newAttachmentId: newAttachmentRandomId,
        modalityAttachmentId: modalityAttachmentRandomId,
        metadata: {
          ..._.get(selectedAttachmentData, "metadata", {}),
          version: nextVersion,
        },
        ...(customMetadata && { customMetadata }),
        authorType: "user",
      });

      // update attachment selection in the dropdown
      onModalityChange(newAttachmentRandomId);

      // create copy of the attachment in the backend
      const attachmentName = _.get(selectedAttachmentData, "name", "");

      let createPayload;
      switch (attachmentType) {
        case "TODDLE_DOC": {
          createPayload = {
            parentId: conversation?.id,
            type: "TODDLE_DOC",
            name: attachmentName,
            toddleDoc: { value },
            parentType: "AI_CONVERSATION",
          };
          break;
        }
        case "CODE_EDITOR": {
          createPayload = {
            parentId: conversation?.id,
            type: "CODE_EDITOR",
            name: attachmentName,
            modalityFile: {
              content: value,
              metadata:
                customMetadata ||
                selectedAttachmentData?.modalityAttachment?.metadata,
              modalityType: attachmentType,
            },
            parentType: "AI_CONVERSATION",
          };
          break;
        }
        case "WORKBOOK": {
          createPayload = {
            parentId: conversation?.id,
            type: "WORKBOOK",
            name: attachmentName,
            url: !_.isEmpty(value?.workbookUrl) ? value.workbookUrl : null,
            metadata: {
              isWorkbookWithPageView: true,
              isNewBackgroundToolbarEnabled: true,
              isNewShapesToolbarEnabled: true,
            },
            parentType: "AI_CONVERSATION",
          };
          break;
        }
      }
      if (!createPayload) {
        // Nothing to create – remove the entry we just added
        clearTrackedAttachment(presetAttachment);
        return;
      }

      setIsPendingAttachmentUpdate(true);

      try {
        let createdAttachments;
        // Running modalityAttachmentId for this particular duplicate, local to
        // this invocation so parallel duplicates don't stomp on each other.
        let localModalityAttachmentId = null;

        if (attachmentType === "WORKBOOK") {
          let attachmentCreated;
          const updatedPayload = {
            ...createPayload,
            presetAttachment,
            metadata: {
              ...createPayload.metadata,
              version: nextVersion,
            },
          };
          if (
            !_.isEmpty(value?.workbookUrl) ||
            (_.isEmpty(value) &&
              originalId &&
              !_.startsWith(originalId, "NEW_"))
          ) {
            attachmentCreated = await createAITutorModality(updatedPayload);
          } else {
            attachmentCreated = await createAIModalityAttachment(
              updatedPayload
            );
          }
          const createdAttachmentId =
            _.get(_.first(attachmentCreated), "id") ||
            _.get(attachmentCreated, "id");
          let attachmentResponse;
          if (!_.isEmpty(createdAttachmentId)) {
            // create mapping of temp random id → real backend id
            if (!attachmentIdMappingRef.current.has(newAttachmentRandomId)) {
              attachmentIdMappingRef.current.set(
                newAttachmentRandomId,
                createdAttachmentId
              );
            }
            if (_.isEmpty(value?.workbookUrl) && !_.isEmpty(value)) {
              attachmentResponse = await updateAITutorModality({
                id: createdAttachmentId,
                jsonData: value,
              });
            } else {
              attachmentResponse = attachmentCreated;
            }
          }
          createdAttachments = [attachmentResponse];
        } else {
          createdAttachments = await createAIModalityAttachment({
            ...createPayload,
            presetAttachment,
            metadata: {
              ...createPayload.metadata,
              version: nextVersion,
            },
          });
        }

        // update the attachment with the correct id once created
        const createdAttachment = _.first(createdAttachments);
        const newAttachmentId = _.get(createdAttachment, "id");

        if (_.isEmpty(createdAttachments) || !newAttachmentId) {
          // Creation failed – remove tracking entry so user can retry
          clearTrackedAttachment(presetAttachment);
          return;
        }

        let workbookAttachmentData;

        // store newly created attachment data in cache, such that withLoader HOC does not refetch it
        switch (attachmentType) {
          case "TODDLE_DOC": {
            await getToddleDocAttachment({
              id: newAttachmentId,
              filters: { mode: "EDIT" },
            });
            break;
          }
          case "CODE_EDITOR": {
            const attachment = await getAttachmentModality({
              id: newAttachmentId,
            });

            localModalityAttachmentId = _.get(
              attachment,
              "modalityAttachment.id"
            );
            break;
          }
          case "WORKBOOK": {
            const attachment = await getAttachment({
              id: newAttachmentId,
            });

            workbookAttachmentData = attachment;

            workbookAttachmentData = _.omit(workbookAttachmentData, [
              "id", // if not removed will replace new_id & will cause reload
              "isRead",
              "lockingState",
            ]);

            localModalityAttachmentId = _.get(attachment, "id");
            break;
          }
        }

        createdAttachmentsMapRef.current.set(presetAttachment, {
          tempId: newAttachmentRandomId,
          attachmentId: newAttachmentId,
          modalityAttachmentId: localModalityAttachmentId,
        });

        onLocalAttachmentsChange(prevAttachments => {
          return _.map(prevAttachments, attachment => {
            if (attachment.id === newAttachmentRandomId) {
              return {
                ...attachment,
                ...workbookAttachmentData,
                originalAttachmentId: newAttachmentId,
                modalityAttachment: {
                  ...attachment.modalityAttachment,
                  ...(localModalityAttachmentId && {
                    id: localModalityAttachmentId,
                  }),
                },
              };
            }
            return attachment;
          });
        });

        await updateAttachmentWrapper(
          newAttachmentId,
          attachmentType,
          modalityValueRef.current,
          localModalityAttachmentId
        );
      } catch {
        // On error clear the entry so the user can retry
        clearTrackedAttachment(presetAttachment);
      }
    },
    [
      options,
      isTeacherChat,
      handleModalityUpdate,
      selectedAttachmentData,
      createAIModalityAttachment,
      onModalityChange,
      getToddleDocAttachment,
      getAttachmentModality,
      setIsPendingAttachmentUpdate,
      onLocalAttachmentsChange,
      conversation?.id,
      updateAITutorModality,
      updateAttachmentWrapper,
      clearTrackedAttachment,
      createAITutorModality,
    ]
  );

  // ============= unified modality change handler ===========
  const handleModalityContentChange = useCallback(
    async (id, value) => {
      if (_.isNil(value)) return;

      // mark pending update as soon as a change occurs
      setIsPendingAttachmentUpdate(true);

      if (shouldUpdateAttachmentWithoutCreatingDuplicate) {
        debouncedUpdateRef.current(
          attachmentId,
          attachmentType,
          value,
          modalityAttachmentId,
          true
        );
        return;
      }

      if (shouldCreateNewAttachmentRef.current) {
        await createDuplicateAttachment(id, value);
      } else {
        const existingAttachment = _.find(localAttachments, { id });

        let originalAttachmentId = _.get(
          existingAttachment,
          "originalAttachmentId"
        );

        let modalityAttachmentId = _.get(
          existingAttachment,
          "modalityAttachment.id"
        );

        if (
          !originalAttachmentId ||
          _.startsWith(originalAttachmentId, "NEW_")
        ) {
          // The preset id is stored on the local attachment itself
          const presetId = _.get(existingAttachment, "presetAttachment.id");
          const tracked = presetId
            ? createdAttachmentsMapRef.current.get(presetId)
            : undefined;
          originalAttachmentId = tracked?.attachmentId || null;
        }

        if (
          !modalityAttachmentId ||
          _.startsWith(modalityAttachmentId, "NEW_")
        ) {
          const presetId = _.get(existingAttachment, "presetAttachment.id");
          const tracked = presetId
            ? createdAttachmentsMapRef.current.get(presetId)
            : undefined;
          modalityAttachmentId = tracked?.modalityAttachmentId || null;
        }

        handleModalityUpdate({
          id,
          value,
          originalAttachmentId,
          modalityAttachmentId,
        });
      }
    },
    [
      localAttachments,
      handleModalityUpdate,
      createDuplicateAttachment,
      setIsPendingAttachmentUpdate,
    ]
  );

  const handleToddleDocOnChange = useCallback(
    async ({ id, toddleDoc }) => {
      if (!isEditMode) return;

      const { childAttachments } = toddleDoc || {};

      if (!_.isEmpty(childAttachments)) return;

      await handleModalityContentChange(id, toddleDoc?.value);
    },
    [isEditMode, modalityValue, handleModalityContentChange]
  );

  const handleCodeChange = useCallback(
    async updatedCode => {
      if (!isEditMode) return;

      await handleModalityContentChange(
        selectedAttachmentData?.id,
        updatedCode
      );
    },
    [isEditMode, handleModalityContentChange, selectedAttachmentData?.id]
  );

  const handleDrawingAttachmentsChange = useCallback(
    async jsonValue => {
      const active = _.get(window, "__ACTIVE_WORKBOOK_IFRAME_ID");
      if (active && active !== myIframeIdRef.current) return;
      if (!isEditMode) return;
      const updatedValue = _.get(jsonValue, "workbookData", jsonValue);
      await handleModalityContentChange(
        selectedAttachmentData?.id,
        updatedValue
      );
    },
    [isEditMode, selectedAttachmentData?.id, handleModalityContentChange]
  );

  const handleRestoreVersion = async () => {
    const attachmentType = _.get(selectedAttachmentData, "type");
    const attachmentId = _.get(selectedAttachmentData, "id");

    // Reset refs to allow creating a new duplicate
    clearTrackedAttachment(attachmentId);

    switch (attachmentType) {
      case "TODDLE_DOC":
      case "CODE_EDITOR": {
        const content = _.get(
          selectedAttachmentData,
          "modalityAttachment.content"
        );
        await createDuplicateAttachment(attachmentId, content);
        break;
      }
      case "WORKBOOK": {
        const workbookUrl =
          _.get(selectedAttachmentData, "url") ||
          _.get(selectedAttachmentData, "metadata.workbookUrl");
        if (workbookUrl) {
          await createDuplicateAttachment(attachmentId, {
            workbookUrl,
          });
        } else if (attachmentId && !_.startsWith(attachmentId, "NEW_")) {
          createDuplicateAttachment(attachmentId, null);
        }
        break;
      }
      default:
        break;
    }
  };

  const invokeWorkbookCustomLoader = useCallback(({ isLoading }) => {
    if (isLoading) {
      setIsWorkbookLoading(true);
      // stop loading after 3 sec
      setTimeout(() => {
        setIsWorkbookLoading(prev => {
          if (prev) {
            return false;
          }
          return prev;
        });
      }, 3000);
    } else {
      setIsWorkbookLoading(false);
    }
  }, []);

  // ============= memoized workbook source ===========
  // used to avoid reloads on every auto save url change
  const workbookSource = useMemo(() => {
    const currentLoadedWrbkData = initialWorkbookUrlRef.current;

    if (!isEditMode) {
      // in view mode clear ref
      initialWorkbookUrlRef.current = {
        url: null,
        attachmentId: null,
        previousAttachmentId: null,
      };
      return selectedAttachmentData;
    }

    if (
      _.isEmpty(currentLoadedWrbkData.url) &&
      _.isEmpty(currentLoadedWrbkData.attachmentId)
    ) {
      initialWorkbookUrlRef.current = {
        url: _.get(selectedAttachmentData, "url"),
        attachmentId: _.get(selectedAttachmentData, "id", ""),
        previousAttachmentId: _.get(selectedAttachmentData, "id", ""),
      };

      return selectedAttachmentData;
    }

    let shouldUseInitialUrl = false;

    // isEditMode

    // check if previous attachment is set
    const refData = initialWorkbookUrlRef.current;
    const selectedAttachmentId = _.get(selectedAttachmentData, "id", "");
    /*
      id used to load workbook at first load; we need to reuse this url to avoid iframe init_url change
      */
    const initialisedWrbkId = _.get(refData, "attachmentId", "");
    const previousSelectedWrbkId = _.get(refData, "previousAttachmentId", ""); // previously selected attachment id
    const selectedAttachmentPresetId = _.get(
      selectedAttachmentData,
      "presetAttachment.id",
      ""
    );

    if (previousSelectedWrbkId) {
      const previousAttachmentOriginalId = _.startsWith(
        previousSelectedWrbkId,
        "NEW_"
      )
        ? attachmentIdMappingRef.current?.get?.(previousSelectedWrbkId) ||
          previousSelectedWrbkId
        : previousSelectedWrbkId;
      const isPrevAttachmentDeleted = !_.some(
        attachments,
        attachment =>
          attachment?.id == previousAttachmentOriginalId ||
          attachment?.originalAttachmentId == previousAttachmentOriginalId
      );
      if (isPrevAttachmentDeleted) {
        initialWorkbookUrlRef.current = {
          url: _.get(selectedAttachmentData, "url"),
          attachmentId: _.get(selectedAttachmentData, "id", ""),
          previousAttachmentId: _.get(selectedAttachmentData, "id", ""),
        };

        if (workbookCompRef?.current) {
          //force reload incase previous attachment is deleted to reset workbook state
          // doing force reload if url is null , then setting same url does not reload workbook
          workbookCompRef.current.forceReload?.();
        }

        return selectedAttachmentData;
      }
    }
    //different cases:
    //1. on new template copy is created on first time draw - id changes from template id to new id
    //2. on send new_ id is replaced with original id
    //3. on auto save, id remains same but url changes with ?v1 etc
    //4. user switched manually to different attachment - reset to selected attachment url
    //5. switch from edit to view mode and back - id remains same

    if (selectedAttachmentId == previousSelectedWrbkId) {
      // re-renders case & case 3
      shouldUseInitialUrl = true;
    } else if (selectedAttachmentId === initialisedWrbkId) {
      //if current is same as initialised wrbk, re-renders case
      shouldUseInitialUrl = true;
    } else if (_.startsWith(selectedAttachmentId, "NEW_")) {
      const orginalSelectedAttachmentId = attachmentIdMappingRef.current?.get(
        selectedAttachmentId
      );
      if (orginalSelectedAttachmentId) {
        const prevOrginalId =
          _.startsWith(previousSelectedWrbkId, "NEW_") &&
          attachmentIdMappingRef.current?.get(previousSelectedWrbkId)
            ? attachmentIdMappingRef.current?.get(previousSelectedWrbkId)
            : previousSelectedWrbkId;
        if (orginalSelectedAttachmentId == prevOrginalId) {
          //case 1 & 2
          shouldUseInitialUrl = true;
        } else if (
          prevOrginalId == initialisedWrbkId ||
          prevOrginalId == previousSelectedWrbkId
        ) {
          shouldUseInitialUrl = true;
        }
      } else if (
        selectedAttachmentPresetId == initialisedWrbkId ||
        selectedAttachmentPresetId == previousSelectedWrbkId
      ) {
        shouldUseInitialUrl = true;
      }

      //TODO case 5, where all remains same but mode changes
    }

    if (!shouldUseInitialUrl) {
      if (_.startsWith(previousSelectedWrbkId, "NEW_")) {
        if (
          selectedAttachmentPresetId == initialisedWrbkId ||
          selectedAttachmentId ==
            attachmentIdMappingRef.current?.get(previousSelectedWrbkId)
        ) {
          shouldUseInitialUrl = true;
        }
      }
    }

    initialWorkbookUrlRef.current.previousAttachmentId = _.get(
      selectedAttachmentData,
      "id",
      ""
    );

    if (shouldUseInitialUrl) {
      const prevWrbkUrl = _.get(initialWorkbookUrlRef.current, "url");
      return {
        ...selectedAttachmentData,
        url: prevWrbkUrl,
        metadata: {
          ..._.get(selectedAttachmentData, "metadata", {}),
          workbookUrl: prevWrbkUrl,
        },
      };
    } else {
      //case 4
      initialWorkbookUrlRef.current = {
        ...initialWorkbookUrlRef.current,
        url: _.get(selectedAttachmentData, "url"),
        attachmentId: _.get(selectedAttachmentData, "id", ""),
      };
    }
    return selectedAttachmentData;
  }, [
    selectedAttachmentData?.id,
    selectedAttachmentData?.url,
    initialWorkbookUrlRef.current?.attachmentId,
    initialWorkbookUrlRef.current?.url,
    isEditMode,
  ]);

  // ============= render functions ===========

  const renderModality = () => {
    if (!selectedAttachmentData) {
      return null;
    }
    const iFrameId =
      source === "STUDENT_PREVIEW"
        ? WORKBOOK_IFRAME_ID_SELECTORS.AI_TUTOR_STUDENT_PREVIEW
        : WORKBOOK_IFRAME_ID_SELECTORS.AI_TUTOR;
    const workbookMode = isViewMode
      ? WORKBOOK_MODE_TYPES.AI_TUTOR_VIEW_V2
      : WORKBOOK_MODE_TYPES.AI_TUTOR_V2;

    const attachmentType = _.get(selectedAttachmentData, "type");
    switch (attachmentType) {
      case "WORKBOOK": {
        const isViewMode = !isEditMode;

        return (
          <div className={classes.workbookContainer}>
            {isWorkbookLoading ? (
              <div className={classes.loadingOverlay}>
                <Loading position="center" />
              </div>
            ) : null}
            <Workbook
              attachment={workbookSource}
              onClose={onClose}
              workbookMode={workbookMode}
              workbookCompRef={workbookCompRef}
              isFullScreen={false}
              shouldEnablePreload={false}
              disableArr={isViewMode ? WORKBOOK_VIEW_MODE_DISABLE_OPTIONS : []}
              iframeIdSelector={iFrameId}
              invokeWorkbookCustomLoader={invokeWorkbookCustomLoader} // add to prevent full screen loader
              onDiscardDirectly={onClose}
              shouldShowViewInstructions={false}
              onChangeData={
                isViewMode ? _.noop : handleDrawingAttachmentsChange
              }
              updateAttachments={handleDrawingAttachmentsChange}
            />
          </div>
        );
      }

      case "TODDLE_DOC": {
        return (
          <ToddleDoc
            attachment={selectedAttachmentData}
            onClose={onClose}
            onChangeAttachment={handleToddleDocOnChange}
            isEvaluation={true}
            mode={mode}
            isInitialContentChangeIgnored={true}
            docEditorConfig={MODALITY_DOC_EDITOR_CONFIG}
            containerStyle={styles.toddleDocContainerStyle}
            nodesDisabled={NODES_DISABLED_FOR_TODDLE_DOC}
            defaultTagSizes={DEFAULT_TAG_SIZES_FOR_TODDLE_DOC}
            width={"100%"}
            nodeConfig={NODE_CONFIG}
            isCommentView={false}
            isAITutor={true}
          />
        );
      }

      case "CODE_EDITOR": {
        const { id, modalityAttachment } = selectedAttachmentData || {};
        const { content: code, metadata } = modalityAttachment || {};
        const { language } = metadata || {};

        return (
          <CodeEditorWrapper
            code={code}
            mode={mode}
            language={language}
            onChange={handleCodeChange}
            attachmentId={id}
          />
        );
      }
      case "VIDEO":
      case "LINK": {
        const { url, metadata } = selectedAttachmentData || {};
        const checkPoints = _.get(metadata, "checkPoints", []);
        const transcriptUrl = _.get(
          metadata,
          "extractedContentDetails.url",
          ""
        );

        // Find the last divider message
        const lastDividerMessage = _.findLast(
          messages,
          message => _.get(message, "node.authorType") === "divider"
        );

        const lastCheckpointTime = _.get(
          lastDividerMessage,
          "node.metadata.checkPointTime"
        );

        // Check if there's at least one user message after the last divider
        const lastDividerIndex = _.findLastIndex(
          messages,
          message => _.get(message, "node.authorType") === "divider"
        );

        const hasUserMessageAfterDivider = _.some(
          messages,
          (message, index) => {
            const authorType = _.get(message, "node.authorType");
            return (
              lastDividerIndex !== -1 &&
              authorType === "user" &&
              index > lastDividerIndex
            );
          }
        );

        // Determine blockedAfterCheckpoint
        let blockedAfterCheckpoint = null;
        if (lastCheckpointTime) {
          if (hasUserMessageAfterDivider) {
            // Find the next checkpoint after the last divider's checkpoint
            const lastCheckpointIndex = _.findIndex(
              checkPoints,
              cp => cp?.timestamp === lastCheckpointTime
            );

            if (
              lastCheckpointIndex !== -1 &&
              lastCheckpointIndex < _.size(checkPoints) - 1
            ) {
              blockedAfterCheckpoint =
                checkPoints?.[lastCheckpointIndex + 1]?.timestamp;
            }
          } else {
            // Use the last checkpoint time as is
            blockedAfterCheckpoint = lastCheckpointTime;
          }
        } else {
          // No divider message exists, use the first checkpoint
          blockedAfterCheckpoint = _.first(checkPoints)?.timestamp;
        }

        // Get all acknowledged checkpoints from divider messages
        const acknowledgedCheckpoints = _.compact(
          _.map(messages, message => {
            const authorType = _.get(message, "node.authorType");
            if (authorType === "divider") {
              return _.get(message, "node.metadata.checkPointTime");
            }
            return null;
          })
        );

        const isReactPlayerEnabled = getIsReactPlayerVideoModalityEnabled();

        const VideoModalityComponent = isReactPlayerEnabled
          ? AITutorVideoModalityV2
          : AITutorVideoModality;

        return (
          <VideoModalityComponent
            url={url}
            checkpoints={checkPoints}
            blockedAfterCheckpoint={blockedAfterCheckpoint}
            acknowledgedCheckpoints={acknowledgedCheckpoints}
            onSendMessage={onSendMessage}
            entitySubType={entitySubType}
            isResizing={isResizing}
            videoPlayerRef={videoPlayerRef}
            conversation={conversation}
            mode={mode}
            setIsPendingAttachmentUpdate={setIsPendingAttachmentUpdate}
            shouldBlockImReadyButton={!_.isEmpty(currentAIState)}
            attachmentId={selectedAttachmentData?.id}
            updateAttachment={updateAttachment}
            transcriptUrl={transcriptUrl}
            assessmentId={assessmentId}
            courseId={courseId}
            gradeId={gradeId}
          />
        );
      }

      default:
        return null;
    }
  };

  const onWorkbookAction = action => async () => {
    await workbookCompRef?.current?.sendMessageV2({
      MESSAGE_TOKEN: action,
    });
  };

  const renderAttachmentActions = () => {
    if (selectedAttachmentData?.type === "CODE_EDITOR") {
      const language = _.get(
        selectedAttachmentData,
        "modalityAttachment.metadata.language",
        "javascript"
      );

      const handleActionClick = ({ key }) => {
        switch (key) {
          case "download":
            handleDownloadCode();
            break;
          case "copy":
            handleCopyCode();
            break;
        }
      };

      const selectedLanguageLabel = _.get(
        _.find(CODE_EDITOR_PROGRAMMING_LANGUAGES, { key: language }),
        "label"
      );

      return (
        <>
          <Dropdown
            overlay={
              <DropdownMenu
                value={language}
                options={CODE_EDITOR_PROGRAMMING_LANGUAGES}
                onClick={handleLanguageChange}
                shouldStopPropagation={true}
                isSearchEnabled={true}
                showSelection={true}
                disabled={!isEditMode}
              />
            }
            disabled={!isEditMode}
          >
            <ButtonDisclosure
              testId={`${testId}-language`}
              size={"medium"}
              variant={"neutral"}
              type={"plain"}
              disabled={!isEditMode}
            >
              {selectedLanguageLabel}
            </ButtonDisclosure>
          </Dropdown>

          <Dropdown
            overlay={
              <DropdownMenu
                dsVersion="2.0"
                options={dropdownOptions(t)}
                onClick={handleActionClick}
                testId={`${testId}-actionsDropdown`}
              />
            }
          >
            <IconButton
              dsVersion="2.0"
              icon={<DotsVerticalOutlined />}
              variant={buttonVariant}
              type={buttonType}
              testId={`${testId}-actionsButton`}
            />
          </Dropdown>
        </>
      );
    } else if (selectedAttachmentData?.type === "WORKBOOK") {
      return (
        <>
          {versionHistoryElement ? (
            <div className={classes.seperator}></div>
          ) : null}
          <div className={classes.tools}>
            <Tooltip tooltip={t("common:zoom_out")} theme={tooltipTheme}>
              <IconButton
                icon={<ZoomOutOutlined />}
                variant={buttonVariant}
                type={buttonType}
                onClick={onWorkbookAction("ZOOM_OUT")}
                testId={`${testId}-zoomout-button`}
                theme={"light"}
              />
            </Tooltip>
            <Tooltip tooltip={t("common:zoom_in")} theme={tooltipTheme}>
              <IconButton
                icon={<ZoomInOutlined />}
                variant={buttonVariant}
                type={buttonType}
                onClick={onWorkbookAction("ZOOM_IN")}
                testId={`${testId}-zoomin-button`}
                theme={"light"}
              />
            </Tooltip>
          </div>
          {isEditMode ? (
            <>
              <div className={classes.seperator}></div>
              <div className={classes.tools}>
                <Tooltip tooltip={t("toastMsgs:undo")} theme={tooltipTheme}>
                  <IconButton
                    icon={<ShareCurveOutlined />}
                    variant={buttonVariant}
                    type={buttonType}
                    onClick={onWorkbookAction("UNDO")}
                    testId={`${testId}-undo-button`}
                    theme={"light"}
                    className={classes.mirror}
                  />
                </Tooltip>
                {/* ADD appropriate locale */}
                <Tooltip tooltip={"Redo"} theme={tooltipTheme}>
                  <IconButton
                    icon={<ShareCurveOutlined />}
                    variant={buttonVariant}
                    type={buttonType}
                    onClick={onWorkbookAction("REDO")}
                    testId={`${testId}-redo-button`}
                    theme={"light"}
                  />
                </Tooltip>
              </div>
            </>
          ) : null}
        </>
      );
    }
    return null;
  };

  const renderEditActions = () => {
    if (isEditingName) {
      return (
        <div className={classes.editActionsContainer}>
          <Tooltip tooltip={t("common:cancel")} theme={tooltipTheme}>
            <IconButton
              icon={<CloseOutlined />}
              variant={buttonVariant}
              type={buttonType}
              size={"small"}
              onClick={handleCancelEdit}
              testId={`${testId}-cancel-edit`}
            />
          </Tooltip>

          <Tooltip tooltip={t("common:save")} theme={tooltipTheme}>
            <IconButton
              icon={<TickLargeOutlined />}
              variant={buttonVariant}
              type={buttonType}
              size={"small"}
              onClick={handleSaveEdit}
              testId={`${testId}-save-edit`}
              disabled={_.isEmpty(_.trim(editedName)) || isEditDisabled}
            />
          </Tooltip>
        </div>
      );
    }

    const createdBy = _.get(selectedAttachmentData, "createdBy.id", null);
    const authorType = _.get(selectedAttachmentData, "authorType", null);

    const currentAttachment = _.find(localAttachments, {
      id: selectedAttachmentData?.id,
    });

    const hasOpeningMsgAttachment =
      currentAttachment?.isOpeningMessageAttachment || false;

    if (
      (createdBy && createdBy !== userId) ||
      authorType === "assistant" ||
      !isEditMode ||
      hasOpeningMsgAttachment ||
      _.isEmpty(currentAttachment) ||
      !_.isEmpty(currentAttachment?.presetAttachment?.id)
    ) {
      return null;
    }

    return (
      <Tooltip tooltip={t("common:edit")} theme={tooltipTheme}>
        <IconButton
          icon={<PencilOutlined />}
          variant={buttonVariant}
          type={buttonType}
          size={"small"}
          onClick={handleStartEdit}
          testId={`${testId}-edit`}
          disabled={isEditDisabled}
        />
      </Tooltip>
    );
  };

  const renderTitleSection = () => {
    const renameTitleClass = classNames(classes.renameContainer, {
      [classes.renameDarkContainer]: showWhiteTheme,
    });

    if (isEditingName) {
      return (
        <div className={renameTitleClass}>
          <TextInput
            value={editedName}
            onChange={handleNameChange}
            placeholder={t("common:untitled")}
            size={"small"}
            autoFocus={true}
            style={styles.renameInputStyle}
          />
        </div>
      );
    }

    if (hasOpeningMsgAttachment || _.size(groupedOptions) <= 1) {
      return (
        <span className={buttonLabelClass}>
          {selectedAttachmentData?.label || t("common:untitled")}
        </span>
      );
    }

    const onClickGroupedOption = ({ key }) => {
      const option = _.find(groupedOptions, { key });
      onClickAttachment({ key: _.last(option?.allIds) || key });
    };

    return (
      <Dropdown
        overlay={
          <DropdownMenu
            value={selectedModalityId}
            options={groupedOptions}
            onClick={onClickGroupedOption}
            shouldStopPropagation={true}
            isSearchEnabled={true}
          />
        }
        overlayStyle={styles.overlayStyle}
      >
        <Tooltip
          tooltip={selectedAttachmentData?.label || t("common:untitled")}
          theme={tooltipTheme}
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <ButtonDisclosure
            testId={`${testId}-attachments`}
            size={"medium"}
            variant={buttonVariant}
            type={buttonType}
            className={classes.buttonDisclosure}
          >
            <span className={classes.textEllipsis}>
              {selectedAttachmentData?.label || t("common:untitled")}
            </span>
          </ButtonDisclosure>
        </Tooltip>
      </Dropdown>
    );
  };

  const renderHeaderLeft = () => {
    return (
      <div className={classes.headerLeft}>
        {isScreenSmall ? (
          <IconButton
            icon={<CloseOutlined />}
            variant={buttonVariant}
            type={buttonType}
            size={"medium"}
            onClick={onClose}
          />
        ) : null}

        {renderTitleSection()}
        {isEditMode && attachmentType !== "LINK" && attachmentType !== "VIDEO"
          ? renderEditActions()
          : null}

        {showSavedLoader && shouldUpdateAttachmentWithoutCreatingDuplicate ? (
          <CircleLoading
            backgroundColor="transparent"
            strokeColor={
              showWhiteTheme ? colors.surfaceWhite : colors.surfaceDark
            }
          />
        ) : null}
      </div>
    );
  };

  const renderVersionHistory = () => {
    if (
      _.size(_.first(versionHistoryOptions)?.options) <= 1 ||
      attachmentType === "LINK" ||
      attachmentType === "VIDEO"
    ) {
      return null;
    }

    return (
      <Dropdown
        overlay={
          <DropdownMenu
            value={selectedModalityId}
            options={versionHistoryOptions}
            onClick={onClickAttachment}
            shouldStopPropagation={true}
            showSelection={true}
          />
        }
        overlayStyle={styles.overlayStyle}
        onVisibleChange={setIsDropdownVisible}
      >
        <Tooltip
          tooltip={t("common:version_history")}
          theme={tooltipTheme}
          visible={!isDropdownVisible}
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <IconButton
            icon={<ClockRecentsOutlined />}
            variant={buttonVariant}
            type={buttonType}
            isActivated={isDropdownVisible}
          />
        </Tooltip>
      </Dropdown>
    );
  };

  const renderOlderVersionAlert = () => {
    const shouldShowAlert =
      showOlderVersionAlert &&
      !_.includes(source, "STUDENT_ASSIGNMENT_SUBMISSION");

    if (
      isEditMode || // if not view mode
      showAIAnalysisLoader || // if analysis is loading
      !_.isEmpty(analysisToolEvaluation?.toolResponses) || // if analysis generated
      !shouldShowAlert ||
      attachmentType === "VIDEO" ||
      attachmentType === "LINK"
    ) {
      return null;
    }

    const attachmentTypeLabel = _.get(
      _.find(AI_TUTOR_LEARNING_TOOLS, { type: attachmentType }),
      "label"
    );

    // Different messages based on whether it's the latest version or an older version
    const alertTitle = isLatestVersion
      ? t("ai:viewing_latest_version_in_view_mode", {
          label: t(attachmentTypeLabel),
        })
      : t("ai:viewing_older_version", {
          label: t(attachmentTypeLabel),
        });

    const alertMessage = isLatestVersion
      ? t("ai:latest_version_view_mode_msg")
      : t("ai:view_mode_msg");

    return (
      <div>
        <Alert
          dsVersion="2.0"
          type="warning"
          title={alertTitle}
          message={alertMessage}
          actionElement={
            <Button
              variant="neutral"
              type="outlined"
              onClick={handleRestoreVersion}
              disabled={isPendingAttachmentUpdate}
            >
              {t("ai:duplicate_version")}
            </Button>
          }
          style={styles.olderVersionAlertStyle}
        />
        <div
          className={
            attachmentType === "CODE_EDITOR" ? classes.darkOverlay : null
          }
        />
      </div>
    );
  };

  const versionHistoryElement = renderVersionHistory();
  const attachmentActionsElement = renderAttachmentActions();

  // ============= classes ===========

  const headerClass = classNames(
    classes.header,
    classes[`${_.camelCase(selectedAttachmentData?.type)}Header`],
    {
      dark: showWhiteTheme,
    }
  );

  const buttonLabelClass = classNames("text-label", classes.textEllipsis, {
    [classes.whiteLabel]: showWhiteTheme,
  });

  return (
    <DSProvider dsVersion={"2.0"}>
      <div
        className={classes.container}
        id="modalities-viewer-container"
        style={containerStyle}
      >
        <header className={headerClass}>
          <div className={classes.headerLeft}>{renderHeaderLeft()}</div>

          <div className={classes.headerRight}>
            {versionHistoryElement}
            {attachmentActionsElement}

            {!isScreenSmall && !hasOpeningMsgAttachment ? (
              <>
                {versionHistoryElement || attachmentActionsElement ? (
                  <div className={classes.seperator} />
                ) : null}

                <IconButton
                  icon={
                    source === "LESSON_PLANNING_ASSISTANT" ? (
                      <CollapseArrowsTwoOutlined />
                    ) : (
                      <CloseOutlined />
                    )
                  }
                  variant={buttonVariant}
                  type={buttonType}
                  onClick={onClose}
                />
              </>
            ) : null}

            {hasOpeningMsgAttachment &&
            source === "STUDENT_ASSIGNMENT_SUBMISSION" ? (
              <IconButton
                icon={<CloseOutlined />}
                variant={buttonVariant}
                type={buttonType}
                onClick={onClose}
              />
            ) : null}

            {isScreenSmall ? (
              <IconButton
                size="small"
                variant={buttonVariant}
                type={buttonType}
                icon={<CaretOpenedFilled style={styles.rotateBy90} />}
                onClick={onToggleModalityContainer}
                testId={`${testId}-collapse`}
              />
            ) : null}
          </div>
        </header>

        {renderOlderVersionAlert()}
        {renderModality()}

        {renameTitleWarning ? (
          <Dialog
            variant="delete-dialog"
            title={t("ai:name_already_in_use_title")}
            message={t("ai:name_already_in_use_msg")}
            buttonOneText={t("common:discard_rename")}
            buttonTwoText={t("common:keep_editing")}
            onButtonOneClick={() => {
              toggleRenameWarning();
              handleCancelEdit();
            }}
            onButtonTwoClick={toggleRenameWarning}
            onClose={toggleRenameWarning}
          />
        ) : null}
      </div>
    </DSProvider>
  );
});

ModalitiesViewer.displayName = "ModalitiesViewer";

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const userId = _.get(userInfo, "id", null);

  const showAIAnalysisLoader = _.get(
    state,
    "classRoom.evaluation.showAIAnalysisLoader",
    false
  );

  return { userId, showAIAnalysisLoader };
};

const mapActionCreators = {
  setToastMsg,
  updateAttachment,
  createAIModalityAttachment,
  getToddleDocAttachment,
  getAttachmentModality,
  uploadFileV2,
  updateAITutorModality,
  createAITutorModality,
};

export default connect(mapStateToProps, mapActionCreators)(ModalitiesViewer);
